// ИСТОЧНИК ДАННЫХ ЭКРАНОВ CRM: своя база ('own') или зеркало Bitrix ('mirror').
//
// Это ЕДИНСТВЕННОЕ место, где выбирается источник. Экраны о выборе не знают вовсе:
// они зовут те же хуки с теми же параметрами, а api/client.ts спрашивает этот модуль
// и меняет ТОЛЬКО адрес — формы ответов /api/own/* и /api/bx совпадают дословно
// (crm_own_api.py написан под это, совпадение проверено сверками).
//
// Переключение ЯВНОЕ и ОБРАТИМОЕ:
//   • умолчание — 'own': ради этого перенос и затевался, Bitrix отключают;
//   • localStorage[КЛЮЧ_ИСТОЧНИКА]='mirror' — вернуть зеркало на этом браузере
//     (localStorage.removeItem(...) — снова умолчание);
//   • ?src=own|mirror в адресе — поверх localStorage, на одну вкладку, для отладки
//     и сверки «одно и то же с двух источников» двумя вкладками рядом.
//
// Неизвестное значение НЕ подменяется молча на другой источник — работаем по умолчанию:
// опечатка в отладочном параметре не должна тихо менять то, на что смотрит человек.

export type Источник = 'own' | 'mirror'

/**
 * Умолчание — 'own'. Менять его на 'mirror' можно только отдельным осознанным решением
 * владельца: на этой константе стоит тест «умолчание молча сменили» (istochnik.test.ts).
 */
export const УМОЛЧАНИЕ_ИСТОЧНИКА: Источник = 'own'

/** Ключ localStorage. Пишется руками в DevTools — это отладочный рычаг, а не настройка UI. */
export const КЛЮЧ_ИСТОЧНИКА = 'cir_crm_source'

const допустим = (v: unknown): v is Источник => v === 'own' || v === 'mirror'

/** Откуда сейчас читают экраны CRM. Считается на каждый запрос — переключение без перезагрузки. */
export function источник(): Источник {
  try {
    const изАдреса = new URLSearchParams(window.location.search).get('src')
    if (допустим(изАдреса)) return изАдреса
    const изХранилища = window.localStorage.getItem(КЛЮЧ_ИСТОЧНИКА)
    if (допустим(изХранилища)) return изХранилища
  } catch {
    // нет window или storage закрыт — это не повод падать, работаем по умолчанию
  }
  return УМОЛЧАНИЕ_ИСТОЧНИКА
}

// ─────────────── зона видимости: предохранитель для СДЕЛОК ───────────────
//
// /api/bx сужает crm.deal.list/get зоной роли (head видит свой отдел, user — себя;
// web/bff/rights.py, ЧТЕНИЕ). Ручка /api/own/* сегодня проверяет только доступ к разделу
// и зоной НЕ сужает — это правка BFF, то есть общего файла основной сессии.
// Пока её нет, отдавать сделки из своей базы учётке с ограниченной зоной значило бы
// ПОКАЗАТЬ ЕЙ ЧУЖИЕ СДЕЛКИ. Поэтому сделки ходят в 'own' только у учёток без границ
// (scope.kind === 'all' из /api/me); остальные продолжают читать зеркало, где дверь стоит.
// Убрать предохранитель — одной строкой, когда BFF научит /api/own сужению.
//
// Компаний, контактов и судов это не касается: их /api/bx и сегодня не сужает
// (в rights.py поле сужения у них None).

let зона: string | null = null

/** Зовёт api/client.fetchMe при каждом ответе /api/me — до этого зона неизвестна. */
export function запомнитьЗону(kind: string | null | undefined): void {
  зона = kind ?? null
}

/**
 * Сделки можно читать из своей базы: границ у учётки нет (kind 'all') либо BFF прав
 * не присылает вовсе (RIGHTS_ENFORCE=0 — тогда и зеркало не сужается, прятать нечего).
 */
export const сделкиМожноИзСвоей = (): boolean => зона === null || зона === 'all'

/** Для тестов: вернуть модуль в состояние «прав ещё не видели». */
export const сброситьЗону = (): void => {
  зона = null
}

// ─────────────── пустые даты своей базы ───────────────
//
// В своей базе НЕТ времени правки из Bitrix (DATE_MODIFY отдаётся null — см. шапку
// crm_own_api.py: время переноса у всех записей одно, выдавать его за время правки —
// враньё). Экран обязан показать прочерк с объяснением, а не «Invalid Date».
// Прочерк рисует fmtDate (пусто → «—»), объяснение — подсказка отсюда.

export const ПОДСКАЗКА_НЕТ_ПРАВКИ = 'в своей базе времени правки из Bitrix нет'
export const ПОДСКАЗКА_НЕТ_СОЗДАНИЯ = 'в своей базе времени создания из Bitrix нет'

/**
 * title для ячейки с датой: подсказка ТОЛЬКО когда дата пуста и читаем свою базу.
 * У зеркала пустая дата — просто пустая дата, объяснять её «своей базой» было бы неправдой.
 */
export const подсказкаПустойДаты = (
  v: unknown, текст: string = ПОДСКАЗКА_НЕТ_ПРАВКИ,
): string | undefined => (!v && источник() === 'own' ? текст : undefined)
